import { useMemo, useState } from 'react';
import styled from 'styled-components';

import CircularProgress from 'components/shared/CircularProgress';
import { MultiSelect, Select } from 'components/shared/form/InfiniteScrollDropdown';
import useWorkflowLogs from 'hooks/useWorkflowLogs';
import { simpleDateAtTimeWithSeconds } from 'lib/date';
import { WorkflowLogEntry } from 'services/api/workflows';
import { colors } from 'styles/theme/colors';
import { BodySmall } from 'styles/typography';
import LinkIcon from 'svg/LinkIcon';

import { ELIGIBILITY_KINDS, ELIGIBILITY_LABEL } from './CategoryCard';
import { Pill, Tone } from './StatusBadge';

// The referral's activity feed (Figma "Activity Log"): a chronological timeline of who
// did what, when — newest first, filterable by actor type, by person, and by the step
// each entry came from. The chip derives from the actor's kind served by the API.
const KIND_DISPLAY: Record<WorkflowLogEntry['createdBy']['kind'], { label: string; tone: Tone }> = {
  user: { label: 'User', tone: 'info' },
  olio: { label: 'Olio', tone: 'running' },
  integration: { label: 'Integration', tone: 'neutral' },
};

type Option = { value: string; label: string };

const ALL: Option = { value: 'all', label: 'All' };
const TYPE_OPTIONS: Option[] = [ALL, ...Object.entries(KIND_DISPLAY).map(([value, d]) => ({ value, label: d.label }))];

const OTHER_STEP: Option = { value: 'other', label: 'Other' };
const ELIGIBILITY_STEP = 'eligibility';

const kindOf = (actionType: string) => actionType.split('::').pop() ?? actionType;

// The portal page a portal-disposition entry came from; the API serves it only for URL-mode portals.
function portalUrlOf(entry: WorkflowLogEntry): string | null {
  const url = entry.data?.portalUrl;
  return typeof url === 'string' && /^https?:\/\//i.test(url) ? url : null;
}

// The step an entry is filed under, as the referral view names it: the Payer Search + Insurance
// Verification pair is one Benefit Verification row there, so it is one step here too. An entry
// the API files under no step (referral created, documents updated, outcome recorded, a details
// edit spanning steps) files under Other.
function stepOf(entry: WorkflowLogEntry): Option {
  if (!entry.step) return OTHER_STEP;
  const { actionType, label } = entry.step;
  if (ELIGIBILITY_KINDS.has(kindOf(actionType))) return { value: ELIGIBILITY_STEP, label: ELIGIBILITY_LABEL };
  return { value: actionType, label };
}

// `actionTypes` is the referral view's row order (the workflow payload's actions), which the Step
// filter lists its options in.
type Props = { workflowId: string; isTerminal?: boolean; actionTypes?: string[] };

export default function ActivityLog({ workflowId, isTerminal, actionTypes = [] }: Props) {
  const { entries, isLoading, isError } = useWorkflowLogs(workflowId, { isTerminal });
  const [kind, setKind] = useState('all');
  const [actor, setActor] = useState('all');
  const [steps, setSteps] = useState<Option[]>([]);

  // The PEOPLE who appear in this feed, alpha — the design's User filter. Machine
  // actors aren't listed (the Activity Type filter covers them). Keyed by actor id
  // so two people sharing a display name stay distinct filters.
  const actorOptions = useMemo(() => {
    const people = entries.filter((entry) => entry.createdBy.kind === 'user');
    const byId = new Map(people.map((entry) => [entry.createdBy.id, entry.createdBy.name]));
    const options = [...byId].map(([id, name]) => ({ value: id, label: name }));
    return [ALL, ...options.sort((a, b) => a.label.localeCompare(b.label))];
  }, [entries]);

  // The steps that appear in this feed, in the order the referral view shows them; a step with no
  // row there (its action type since left the definition) follows alpha, and Other comes last.
  // Offered only once the API files entries under steps at all.
  const stepOptions = useMemo(() => {
    if (!entries.some((entry) => entry.step)) return [];
    const byValue = new Map(entries.map((entry) => stepOf(entry)).map((option) => [option.value, option]));
    const screenIndex = (option: Option) =>
      actionTypes.findIndex((actionType) =>
        option.value === ELIGIBILITY_STEP ? ELIGIBILITY_KINDS.has(kindOf(actionType)) : actionType === option.value
      );
    const rank = (option: Option): [number, number, string] => {
      if (option.value === OTHER_STEP.value) return [2, 0, ''];
      const index = screenIndex(option);
      return index === -1 ? [1, 0, option.label] : [0, index, ''];
    };
    return [...byValue.values()].sort((a, b) => {
      const [groupA, indexA, labelA] = rank(a);
      const [groupB, indexB, labelB] = rank(b);
      return groupA - groupB || indexA - indexB || labelA.localeCompare(labelB);
    });
  }, [entries, actionTypes]);

  const stepValues = new Set(steps.map((option) => option.value));
  const visible = entries.filter(
    (entry) =>
      (kind === 'all' || entry.createdBy.kind === kind) &&
      (actor === 'all' || entry.createdBy.id === actor) &&
      (stepValues.size === 0 || stepValues.has(stepOf(entry).value))
  );

  if (isLoading)
    return (
      <Centered>
        <CircularProgress color={colors.blue100} />
      </Centered>
    );
  if (isError) return <Muted>We couldn’t load this referral’s activity.</Muted>;

  return (
    <div>
      <Filters>
        <FilterSelect data-testid='activity-type-filter'>
          <Select<Option>
            options={TYPE_OPTIONS}
            value={TYPE_OPTIONS.find((option) => option.value === kind) ?? ALL}
            onChange={(option) => setKind(option?.value ?? 'all')}
            getOptionValue={(option) => option.value}
            getOptionLabel={(option) => option.label}
            formatOptionLabel={(option, meta) =>
              meta?.context === 'value' ? `Activity Type: ${option.label}` : option.label
            }
          />
        </FilterSelect>
        <FilterSelect data-testid='activity-user-filter'>
          <Select<Option>
            options={actorOptions}
            value={actorOptions.find((option) => option.value === actor) ?? ALL}
            onChange={(option) => setActor(option?.value ?? 'all')}
            getOptionValue={(option) => option.value}
            getOptionLabel={(option) => option.label}
            formatOptionLabel={(option, meta) => (meta?.context === 'value' ? `User: ${option.label}` : option.label)}
          />
        </FilterSelect>
        {stepOptions.length > 0 && (
          <FilterSelect data-testid='activity-step-filter'>
            <MultiSelect<Option>
              options={stepOptions}
              value={steps}
              onChange={setSteps}
              placeholder='Step'
              getOptionValue={(option) => option.value}
              getOptionLabel={(option) => option.label}
              formatOptionLabel={(option) => <span data-testid='step-option'>{option.label}</span>}
            />
          </FilterSelect>
        )}
      </Filters>

      <Timeline>
        {visible.map((entry, index) => {
          const chip = KIND_DISPLAY[entry.createdBy.kind] ?? KIND_DISPLAY.user;
          const portalUrl = portalUrlOf(entry);
          return (
            <Row key={entry.id} data-testid='activity-entry'>
              <Marker>
                <Ring />
                {index < visible.length - 1 && <Connector />}
              </Marker>
              <RowMain>
                <RowHeadline>
                  <Pill $tone={chip.tone}>{chip.label}</Pill>
                  <Summary>{entry.summary}</Summary>
                </RowHeadline>
                {entry.detail && <Detail data-testid='activity-detail'>{entry.detail}</Detail>}
                {portalUrl && (
                  <PortalLink
                    href={portalUrl}
                    target='_blank'
                    rel='noreferrer noopener'
                    data-testid='activity-portal-link'>
                    View in portal
                    <LinkIcon width={10} height={10} color={colors.blue100} />
                  </PortalLink>
                )}
                <Meta>
                  {/* Machine entries wear their chip; a "by" line only makes sense for people. */}
                  {simpleDateAtTimeWithSeconds(entry.createdAt)}
                  {entry.createdBy.kind === 'user' && ` by ${entry.createdBy.name}`}
                  {entry.step && ` · ${stepOf(entry).label}`}
                </Meta>
              </RowMain>
            </Row>
          );
        })}
        {visible.length === 0 && <Muted data-testid='activity-empty'>No activity to show.</Muted>}
      </Timeline>
    </div>
  );
}

const Centered = styled.div`
  display: flex;
  justify-content: center;
  padding: 48px 0;
`;

const Filters = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
`;

const FilterSelect = styled.div`
  min-width: 200px;
`;

const Timeline = styled.div`
  display: flex;
  flex-direction: column;
`;

const Row = styled.div`
  display: flex;
  gap: 14px;
`;

const Marker = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 16px;
  flex-shrink: 0;
  padding-top: 3px;
`;

const Ring = styled.span`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 1.5px solid ${colors.blue100};
  flex-shrink: 0;
`;

const Connector = styled.span`
  width: 1.5px;
  flex: 1;
  min-height: 18px;
  margin: 4px 0;
  background: ${colors.black10};
`;

const RowMain = styled.div`
  padding-bottom: 22px;
  min-width: 0;
`;

const RowHeadline = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Summary = styled(BodySmall)`
  color: ${colors.black100};
  font-size: 14px;
`;

const Detail = styled(BodySmall)`
  display: block;
  margin-top: 2px;
  color: ${colors.black75};
`;

const PortalLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 2px;
  font-size: 12px;
  color: ${colors.blue100};
  text-decoration: underline;
`;

const Meta = styled(BodySmall)`
  display: block;
  margin-top: 4px;
  color: ${colors.black50};
  font-size: 12px;
`;

const Muted = styled(BodySmall)`
  color: ${colors.black50};
`;
